import { Component, Input, OnInit } from '@angular/core';
import { IApplicationFiltersExtended, IFilterOption, IPriceRange } from '../../../../shared/shared.definitions';
import { CollectionFilterService } from '../../collection-filter.service';

const ADDITIONAL_REGIONS_LABEL_HU = 'További térségek';
const ADDITIONAL_REGIONS_LABEL_EN = 'Additional regions';

@Component({
  selector: 'app-collection-filters',
  templateUrl: './collection-filters.component.html',
  styleUrls: ['./collection-filters.component.scss'],
})
export class CollectionFiltersComponent implements OnInit {
  @Input() isAccommodationCampaign = false;
  @Input() type?: string;
  @Input() uuid?: string;
  @Input() componentUuid?: string;

  isCityFilterOpen = false;
  isaccommodationTypeFilterOpen = false;
  isaccommodationClassFilterOpen = false;
  isAccommodationFeatureFilterOpen = false;
  isAccommodationServiceFilterOpen = false;
  isCampaignOffersFilterOpen = false;
  isaccommodationNatureFilterOpen = false;
  isPaymentMethodFilterOpen = false;
  isEquipmentFilterOpen = false;

  priceRangeDisplayStart = 0;
  priceRangeDisplayEnd = 0;
  discountPercentDisplayStart = 0;
  discountPercentDisplayEnd = 0;

  accommodationNatureOptions: Array<{ key: keyof IApplicationFiltersExtended['accommodationNature']; label: string }> = [
    { key: 'accommodationForFamilies', label: 'CAMPAIGN.nature.families' },
    { key: 'accommodationForCouples', label: 'CAMPAIGN.nature.couples' },
    { key: 'isAccessible', label: 'CAMPAIGN.nature.accessible' },
  ];

  equipmentOptions: Array<{ key: keyof IApplicationFiltersExtended['equipment']; label: string }> = [
    { key: 'acceptsSzepCard', label: 'CAMPAIGN.equipment.acceptsSzepCard' },
    { key: 'has24HourReception', label: 'CAMPAIGN.equipment.has24HourReception' },
    { key: 'hasRestaurant', label: 'CAMPAIGN.equipment.hasRestaurant' },
    { key: 'idealForCouples', label: 'CAMPAIGN.equipment.idealForCouples' },
    { key: 'petFriendly', label: 'CAMPAIGN.equipment.petFriendly' },
    { key: 'freeParking', label: 'CAMPAIGN.equipment.freeParking' },
    { key: 'paidParking', label: 'CAMPAIGN.equipment.paidParking' },
    { key: 'freeWifi', label: 'CAMPAIGN.equipment.freeWifi' },
    { key: 'hasElevator', label: 'CAMPAIGN.equipment.hasElevator' },
    { key: 'hasWellnessServices', label: 'CAMPAIGN.equipment.hasWellnessServices' },
    { key: 'hasPrivateBeach', label: 'CAMPAIGN.equipment.hasPrivateBeach' },
    { key: 'hasOutdoorPool', label: 'CAMPAIGN.equipment.hasOutdoorPool' },
    { key: 'hasIndoorPool', label: 'CAMPAIGN.equipment.hasIndoorPool' },
    { key: 'hasJacuzzi', label: 'CAMPAIGN.equipment.hasJacuzzi' },
    { key: 'hasSauna', label: 'CAMPAIGN.equipment.hasSauna' },
    { key: 'hasInfraSauna', label: 'CAMPAIGN.equipment.hasInfraSauna' },
    { key: 'hasFitness', label: 'CAMPAIGN.equipment.hasFitness' },
    { key: 'familyFriendly', label: 'CAMPAIGN.equipment.familyFriendly' },
    { key: 'hasOutdoorPlayground', label: 'CAMPAIGN.equipment.hasOutdoorPlayground' },
    { key: 'hasIndoorPlayArea', label: 'CAMPAIGN.equipment.hasIndoorPlayArea' },
    { key: 'hasKidsPool', label: 'CAMPAIGN.equipment.hasKidsPool' },
    { key: 'hasCrib', label: 'CAMPAIGN.equipment.hasCrib' },
    { key: 'hasChildcare', label: 'CAMPAIGN.equipment.hasChildcare' },
  ];

  constructor(readonly filterService: CollectionFilterService) {
    const initialFilters = this.filterService.getFilters();
    this.priceRangeDisplayStart = initialFilters?.priceRange?.start ?? 0;
    this.priceRangeDisplayEnd = initialFilters?.priceRange?.end ?? 0;
    const initialOfferMin = initialFilters?.discountPercent?.min ?? 0;
    const initialOfferMax = initialFilters?.discountPercent?.max ?? 100;
    const initialOfferStart = initialFilters?.discountPercent?.start ?? initialOfferMin;
    const initialOfferEnd = initialFilters?.discountPercent?.end ?? initialOfferMax;
    this.discountPercentDisplayStart = Math.min(Math.max(initialOfferStart, initialOfferMin), initialOfferMax);
    this.discountPercentDisplayEnd = Math.min(Math.max(initialOfferEnd, initialOfferMin), initialOfferMax);
  }

  ngOnInit(): void {
    this.filterService.filters$.subscribe((filters) => {
      this.priceRangeDisplayStart = filters?.priceRange?.start ?? 0;
      this.priceRangeDisplayEnd = filters?.priceRange?.end ?? 0;
      if (filters?.discountPercent) {
        const min = filters.discountPercent.min ?? 0;
        const max = filters.discountPercent.max ?? 100;
        const start = filters.discountPercent.start ?? min;
        const end = filters.discountPercent.end ?? max;
        this.discountPercentDisplayStart = Math.min(Math.max(start, min), max);
        this.discountPercentDisplayEnd = Math.min(Math.max(end, min), max);
      }
    });

    // Fetch price range from API if type and uuid are provided and price range is not already set
    if (
      this.type &&
      this.uuid &&
      this.componentUuid &&
      (!this.filterService.getFilters().priceRange || !this.filterService.getFilters().discountPercent)
    ) {
      this.filterService.fetchFilterOptions(this.type as string, this.uuid as string, this.componentUuid as string);
    }
  }

  filterActive(filters: IFilterOption[]): any {
    return filters.findIndex((f) => f.checked) > -1;
  }

  onClearFilter(filterType: keyof IApplicationFiltersExtended): void {
    this.filterService.resetFilter(filterType);
  }

  onOptionClick(option: IFilterOption): void {
    option.checked = !option.checked;

    this.filterService.updateFilters(JSON.parse(JSON.stringify(this.filterService.getFilters())));
  }

  onAccommodationNatureToggle(key: keyof IApplicationFiltersExtended['accommodationNature']): void {
    const currentFilters = JSON.parse(JSON.stringify(this.filterService.getFilters()));
    currentFilters.accommodationNature[key] = !currentFilters.accommodationNature[key];
    this.filterService.updateFilters(currentFilters);
  }

  onEquipmentToggle(key: keyof IApplicationFiltersExtended['equipment']): void {
    const currentFilters = JSON.parse(JSON.stringify(this.filterService.getFilters()));
    currentFilters.equipment[key] = !currentFilters.equipment[key];
    this.filterService.updateFilters(currentFilters);
  }

  accommodationClassText(text: string): string {
    return text.trim().endsWith('𝐒') ? text + 'uperior' : text;
  }

  isHotelSelected(activeFilters: any): boolean {
    return activeFilters.accommodationType.some((item: any) => item.id === 1 && item.checked === true);
  }

  isaccommodationTypeUnchecked(activeFilters: any): boolean {
    return activeFilters.accommodationType.every((item: any) => item.checked === false);
  }

  isOnlyNonClassifiedSelected(activeFilters: any): boolean {
    const checkedItems = (activeFilters.accommodationType as IFilterOption[]).filter((item: IFilterOption) => item.checked);

    return checkedItems.every((item: IFilterOption) => item.id === 6 || item.id === 7) && checkedItems.length > 0;
  }

  getPriceRangeSliderMax(range: IPriceRange): number {
    const step = 1000;
    return range.min + Math.ceil((range.max - range.min) / step) * step;
  }

  onPriceRangeChange(): void {
    const currentFilters = JSON.parse(JSON.stringify(this.filterService.getFilters()));
    const min = currentFilters.priceRange?.min ?? 0;
    const max = currentFilters.priceRange?.max ?? 0;
    const clampedStart = Math.max(min, Math.min(this.priceRangeDisplayStart, max));
    const clampedEnd = Math.max(min, Math.min(this.priceRangeDisplayEnd, max));

    (currentFilters as any).priceRange = {
      ...((currentFilters as any).priceRange || {}),
      start: Math.min(clampedStart, clampedEnd),
      end: Math.max(clampedStart, clampedEnd),
    };

    this.filterService.updateFilters(currentFilters as unknown as any);
  }

  onPriceRangeInput(event: any, which: 'start' | 'end'): void {
    const raw = event?.target?.value;
    const parsed = raw !== undefined && raw !== null ? Number(raw) : null;

    const currentFilters = JSON.parse(JSON.stringify(this.filterService.getFilters()));

    if (!currentFilters.priceRange) {
      currentFilters.priceRange = { min: 0, max: 0, start: null, end: null };
    }

    if (which === 'start') {
      this.priceRangeDisplayStart = Number.isFinite(parsed) ? parsed : currentFilters.priceRange.start;
    } else {
      this.priceRangeDisplayEnd = Number.isFinite(parsed) ? parsed : currentFilters.priceRange.end;
    }
  }

  onDiscountPercentChange(): void {
    const currentFilters = JSON.parse(JSON.stringify(this.filterService.getFilters()));
    const range = (currentFilters as any).discountPercent || { min: 0, max: 100, start: 0, end: 100 };
    const min = range.min ?? 0;
    const max = range.max ?? 100;
    const clampedStart = Math.max(min, Math.min(this.discountPercentDisplayStart, max));
    const clampedEnd = Math.max(min, Math.min(this.discountPercentDisplayEnd, max));
    const start = Math.min(clampedStart, clampedEnd);
    const end = Math.max(clampedStart, clampedEnd);

    this.discountPercentDisplayStart = start;
    this.discountPercentDisplayEnd = end;

    (currentFilters as any).discountPercent = {
      ...range,
      start,
      end,
    };

    this.filterService.updateFilters(currentFilters as unknown as any);
  }

  onDiscountPercentInput(event: any, which: 'start' | 'end'): void {
    const raw = event?.target?.value;
    const parsed = raw !== undefined && raw !== null ? Number(raw) : null;

    const currentFilters = JSON.parse(JSON.stringify(this.filterService.getFilters()));

    if (!currentFilters.discountPercent) {
      currentFilters.discountPercent = { min: 0, max: 100, start: null, end: null };
    }

    const min = currentFilters.discountPercent.min ?? 0;
    const max = currentFilters.discountPercent.max ?? 100;
    const clamp = (value: number | null, fallback: number | null): number | null => {
      if (typeof value !== 'number' || !Number.isFinite(value)) {
        return fallback;
      }
      return Math.max(min, Math.min(value, max));
    };

    if (which === 'start') {
      this.discountPercentDisplayStart = clamp(parsed, currentFilters.discountPercent.start) ?? min;
    } else {
      this.discountPercentDisplayEnd = clamp(parsed, currentFilters.discountPercent.end) ?? max;
    }
  }

  shouldShowOptionList(options?: IFilterOption[] | null): boolean {
    return Array.isArray(options) && options.length > 0;
  }

  sortFilterOptionsByText(options: IFilterOption[] | null | undefined): IFilterOption[] {
    if (!options) {
      return [];
    }
    return [...options].sort((a, b) => {
      const aIsExtra = a.text === ADDITIONAL_REGIONS_LABEL_HU;
      const bIsExtra = b.text === ADDITIONAL_REGIONS_LABEL_HU;
      if (aIsExtra && !bIsExtra) {
        return 1;
      }
      if (!aIsExtra && bIsExtra) {
        return -1;
      }
      return a.text.localeCompare(b.text, 'hu');
    });
  }

  getSuburbDisplayText(option: IFilterOption): string {
    if (option.text === ADDITIONAL_REGIONS_LABEL_HU) {
      return ADDITIONAL_REGIONS_LABEL_EN;
    }
    return option.text;
  }

  shouldShowRange(range?: IPriceRange | null): boolean {
    if (!range) {
      return false;
    }

    const min = range.min ?? 0;
    const max = range.max ?? 0;

    return Number.isFinite(min) && Number.isFinite(max) && min < max;
  }

  isFilterVisible(key: string): boolean {
    const visibleFilters = this.filterService.getVisibleFilters();
    return visibleFilters?.[key] ?? false;
  }
}
